// The rails and local strategies are two implementations of one small interface
// and belong together as a cohesive unit.
/* eslint-disable max-classes-per-file */
import { Logger } from '@gitlab-org/logging';
import { toGitLabGid } from '@gitlab-org/core';
import {
  type DuoWorkflowFlowIdentity,
  type DuoWorkflowFlowIdentityNode,
  type DefaultFlowConfig,
  FLOW_METADATA_FRAGMENT,
  GET_WORKFLOW_DEFINITION_QUERY,
  GET_WORKFLOW_FLOW_IDENTITY_QUERY,
  parseRecordedFlowConfig,
  WorkflowRunner,
  WorkflowDefinition,
  resolveWorkflowDefinition,
} from '@gitlab-lsp/workflow-api';
import type { CreateWorkflowOptions, WorkflowId } from '@gitlab-org/workflow-executor';
import type { AgentMode } from '../backend';
import type { WorkflowAgentPrivileges } from './workflow_agent_privileges';
import type { RunCondition } from './run_condition';
import type { GitLabParsedOptions } from './gitlab_parsed_options';
import type { WorkflowState } from './workflow_state';

// Last resort for a developer session that records no coordinates of its own:
// created before 19.3, or resumed against an instance older than 19.3.
export const DEVELOPER_RESUME_FLOW: DefaultFlowConfig = {
  flow_config_id: 'developer',
  flow_config_schema_version: 'v1',
  flow_version: '2.0.0-interactive',
};

// What the record says about the flow the session was CREATED under.
export type ResumedFlowIdentity = {
  definition?: string;
  recordedFlow?: DefaultFlowConfig;
};

/**
 * The resolved flow identity for a session. Resolved once by the strategy at
 * acquire time so every flow decision reads a field off a single value.
 */
export type FlowDescriptor = {
  // Sent to DWS verbatim; may be a custom string outside the well-known
  // definitions. Also scopes the workflow token / capability cache bucket.
  definition: string;
  // Present <=> developer flow, carrying the resolved registry coordinates.
  // FIXME: This is named as developer because that's the only flow we currently use
  // and because we hardcode developer/v1 as the `workflowDefinition` for now
  // when we stop hardcoding developer/v1 throughout the cli, we can rename this to `flowConfig`
  developer?: DefaultFlowConfig;
  // Enable token-by-token UI streaming (developer flow opts into it).
  streaming: boolean;
  // Exclude MCP tools when the active mode is plan (developer flow only).
  excludeMcpToolsInPlan: boolean;
};

function developerDescriptor(developer: DefaultFlowConfig): FlowDescriptor {
  return {
    definition: WorkflowDefinition.DEVELOPER_V1,
    developer,
    streaming: true,
    excludeMcpToolsInPlan: true,
  };
}

// The definition is a workflow_definition string the server routes on (rails maps
// e.g. `code_review/v1` -> the review_merge_request_dap unit primitive / feature
// setting, and buckets only `chat` to a shared token). It is preserved verbatim:
// coercing an out-of-set definition to CHAT would request a wrongly-scoped token
// and record the wrong workflow_definition.
function plainDescriptor(definition: string): FlowDescriptor {
  return { definition, streaming: false, excludeMcpToolsInPlan: false };
}

// Refusal to resume, already phrased for the user; the backend passes it through
// rather than wrapping it in the generic access-denied text.
export class ResumeIdentityError extends Error {}

// An absent node means the id is wrong, deleted, or inaccessible. Returning an
// empty identity would drop the resume into the fresh-session branch, which is
// free to pick the override, the server default, or chat.
function requireNode(
  data: DuoWorkflowFlowIdentity | undefined,
  sessionId: WorkflowId,
): DuoWorkflowFlowIdentityNode {
  const node = data?.duoWorkflowWorkflows?.nodes?.[0];
  if (!node) {
    throw new ResumeIdentityError(
      `Session ${sessionId} was not found. It may have been deleted, or belong to another user or project.`,
    );
  }
  return node;
}

/**
 * Collaborators the strategy needs to create/attach a workflow. A small bag
 * rather than the whole backend, so the strategy is unit-testable in isolation.
 */
export interface WorkflowStrategyContext {
  logger: Logger;
  workflowRunner: WorkflowRunner;
  /** Stamped onto a freshly created workflow (unused when attaching). */
  newWorkflowMetadata: {
    rootNamespaceId: string;
    selectedModelIdentifier: string;
    /** Project GID resolved from the configured project path, when available. */
    projectId?: string;
  };
  privileges: WorkflowAgentPrivileges;
  activeAgentMode: AgentMode | undefined;
  /**
   * Auto-approve agent actions (headless `run` / rails CI, or
   * `--dangerously-skip-permissions`). Resolved once at startup and threaded in
   * so the strategy neither owns nor recomputes it.
   */
  autoApprove: boolean;
}

/** What the chokepoint hands back to the backend. */
export interface AcquiredWorkflow {
  workflow: WorkflowState;
  /** null when the source has no direct_access capabilities (rails/CI). */
  capabilities: string[] | null;
  /** The flow identity resolved by the strategy, source of truth for the session. */
  flow: FlowDescriptor;
}

/**
 * Encapsulates everything that differs about workflow lifecycle between the
 * rails-owned (CI) and CLI-owned (local) sources. Picked once from a
 * {@link RunCondition}, then consumed by the backend at a single chokepoint.
 *
 * Local interactive and local headless share one strategy instance so their
 * lifecycle plumbing is identical; only the controller that drains the event
 * stream differs.
 */
export interface WorkflowStrategy {
  /**
   * The single chokepoint. Create the workflow (local, fresh) or attach to an
   * existing one (rails, or local resume). Runs any per-source setup — flow
   * lookup, capability fetch — exactly once.
   */
  acquireWorkflow(ctx: WorkflowStrategyContext): Promise<AcquiredWorkflow>;
}

/**
 * Headless CI. Attach to the rails-pre-created id. No create, no flow lookup;
 * the pre-configured token exposes no capabilities.
 */
export class RailsPreCreatedWorkflowStrategy implements WorkflowStrategy {
  #workflowId: WorkflowId;

  // The explicit CI flow selector. rails resolves the flow purely from this
  // (no server default-flow lookup), so a CI run never pays for that network call.
  #workflowType?: string;

  constructor(workflowId: WorkflowId, workflowType?: string) {
    this.#workflowId = workflowId;
    this.#workflowType = workflowType;
  }

  async acquireWorkflow(ctx: WorkflowStrategyContext): Promise<AcquiredWorkflow> {
    // The CI selector is the workflow_definition verbatim; rails scopes the token
    // and capabilities from it, so it must not be coerced to a known enum member.
    const flow = plainDescriptor(resolveWorkflowDefinition(this.#workflowType));
    ctx.logger.info(
      `Workflow source=rails (CI pre-created); attaching to ${this.#workflowId} (flow: ${flow.definition}), no local creation`,
    );
    return {
      // Attach as `live`, not `resumed`: the `resumed` state exists only to arm
      // the local resume-probe. A rails workflow is created fresh by the monolith
      // immediately before the CLI attaches, so there is no prior run that could
      // have "ended" and no probe to run; a rails-side terminal state surfaces
      // through the normal checkpoint stream.
      workflow: { type: 'live', id: this.#workflowId },
      capabilities: null,
      flow,
    };
  }
}

/**
 * Interactive TUI + headless `run`. The CLI owns the workflow: create fresh, or
 * attach to a prior local session on resume. This is the ONE place the local
 * creation plumbing lives.
 */
export class LocalWorkflowStrategy implements WorkflowStrategy {
  #resumeWorkflowId?: WorkflowId;

  // Explicit flow selector from a headless `duo run`. When set it wins over the
  // server default (a headless run must not be auto-upgraded to the instance's
  // developer default); undefined for interactive, which always obeys the default.
  #workflowTypeOverride?: string;

  // --legacy: force the classic chat flow for a new interactive session even
  // when the instance advertises a developer default. Temporary opt-out for the
  // Duo Developer rollout.
  #legacy: boolean;

  constructor(resumeWorkflowId?: WorkflowId, workflowTypeOverride?: string, legacy = false) {
    this.#resumeWorkflowId = resumeWorkflowId;
    this.#workflowTypeOverride = workflowTypeOverride;
    this.#legacy = legacy;
  }

  async acquireWorkflow(ctx: WorkflowStrategyContext): Promise<AcquiredWorkflow> {
    if (this.#resumeWorkflowId) {
      return this.#attachResumed(ctx, this.#resumeWorkflowId);
    }
    return this.#createFresh(ctx);
  }

  async #attachResumed(ctx: WorkflowStrategyContext, id: WorkflowId): Promise<AcquiredWorkflow> {
    const resumed = await this.#fetchResumedFlowIdentity(ctx, id);
    const flow = await this.#resolveFlow(ctx, resumed);
    const coords = flow.developer
      ? `, coords: ${flow.developer.flow_config_id}/${flow.developer.flow_config_schema_version}@${flow.developer.flow_version}`
      : '';
    ctx.logger.info(
      `Workflow source=local (resume); attaching to ${id} (definition: ${resumed.definition ?? 'unknown'}, flow: ${flow.definition}${coords})`,
    );
    // No token has been fetched in this process yet, so getServerCapabilities
    // returns null here; the backend's lazy top-up fills them in on the first
    // turn once a token is cached.
    const capabilities = ctx.workflowRunner.getServerCapabilities(id, flow.definition);
    return { workflow: { type: 'resumed', id }, capabilities, flow };
  }

  async #createFresh(ctx: WorkflowStrategyContext): Promise<AcquiredWorkflow> {
    const flow = await this.#resolveFlow(ctx);
    const agentPrivileges = ctx.privileges.privilegesForMode(ctx.activeAgentMode);
    // Send the pre-approved set explicitly (same helper as the resume path) so a
    // fresh session and a resumed session with the same flags are identical. The
    // 'run' command and --dangerously-skip-permissions both auto-approve agent
    // actions (no interactive user to prompt), so they also stop the agent from
    // requesting user input.
    const createWorkflowOptions: CreateWorkflowOptions = {
      agentPrivileges,
      preApprovedAgentPrivileges: ctx.privileges.preApprovedForMode(ctx.activeAgentMode),
      ...(ctx.autoApprove ? { allowAgentToRequestUser: false } : {}),
    };

    const workflowId = await ctx.workflowRunner.preCreateWorkflow(
      '',
      flow.definition,
      undefined,
      ctx.newWorkflowMetadata,
      createWorkflowOptions,
    );
    ctx.privileges.markCreated(ctx.activeAgentMode);

    const capabilities = ctx.workflowRunner.getServerCapabilities(workflowId, flow.definition);
    ctx.logger.info(
      `Workflow source=local (fresh); created ${workflowId} (flow: ${flow.definition}), capabilities=[${capabilities?.join(', ') ?? 'none'}]`,
    );

    return { workflow: { type: 'live', id: workflowId }, capabilities, flow };
  }

  // A property of the session, not the invocation: the flow never depends on the
  // flags passed on resume.
  async #resolveFlow(
    ctx: WorkflowStrategyContext,
    resumed: ResumedFlowIdentity = {},
  ): Promise<FlowDescriptor> {
    const resumedDefinition = resumed.definition;
    // Resume wins: a recorded developer session stays developer even if the
    // instance stopped advertising the default (then use the resume fallback),
    // so an existing chat session is never silently promoted to developer.
    if (resumedDefinition === WorkflowDefinition.DEVELOPER_V1) {
      // A developer session's flow config is baked into its checkpoint, so it
      // can't be replayed under the legacy chat flow. --legacy is temporary, so
      // a clear error is enough (no filtered resumable list).
      if (this.#legacy) {
        throw new ResumeIdentityError(
          'Cannot resume a Duo Developer session with --legacy. Remove --legacy to resume it, or start a new session with /new.',
        );
      }
      return developerDescriptor(await this.#resumeDeveloperFlow(ctx, resumed.recordedFlow));
    }
    if (resumedDefinition) {
      return plainDescriptor(resumedDefinition);
    }
    // Explicit `duo run` flow selection wins over the server default: a headless
    // run passes the flow it wants (workflow_type / --flow-config-id), so we must
    // not auto-upgrade it to the instance's developer default.
    if (this.#workflowTypeOverride) {
      return plainDescriptor(this.#workflowTypeOverride);
    }
    // --legacy forces the classic chat flow: skip the instance's developer
    // default upgrade for a new session. (Resume is handled above.)
    if (this.#legacy) {
      ctx.logger.info('--legacy set; forcing legacy chat flow for new session');
      return plainDescriptor(WorkflowDefinition.CHAT);
    }
    // New session: the instance advertising a default flow is the ONLY thing
    // that makes a fresh session a developer session. The client owns the
    // workflow_definition label.
    const serverDefault = await this.#getServerDefault(ctx);
    if (serverDefault) {
      return developerDescriptor(serverDefault);
    }
    return plainDescriptor(WorkflowDefinition.CHAT);
  }

  // Recorded coordinates beat the advertised default: after a flag flip, rollback
  // or version bump the default is this session's original flow only by luck.
  async #resumeDeveloperFlow(
    ctx: WorkflowStrategyContext,
    recordedFlow?: DefaultFlowConfig,
  ): Promise<DefaultFlowConfig> {
    if (recordedFlow) {
      ctx.logger.info(`Resume flow identity source=recorded: ${JSON.stringify(recordedFlow)}`);
      return recordedFlow;
    }
    const serverDefault = await this.#getServerDefault(ctx);
    if (serverDefault) {
      ctx.logger.info(
        `Resume flow identity source=server-default (session records no coordinates): ${JSON.stringify(serverDefault)}`,
      );
      return serverDefault;
    }
    ctx.logger.warn(
      `Resume flow identity source=hardcoded-fallback: ${JSON.stringify(DEVELOPER_RESUME_FLOW)}. ` +
        'The session records no flow coordinates and the instance advertises no default; ' +
        'if it was created on a different flow version the first prompt may produce no response.',
    );
    return DEVELOPER_RESUME_FLOW;
  }

  // Reads the server-advertised default flow (memoised in the runner, keyed by
  // rootNamespaceId+projectId). Scoped the same way as the capabilities half of
  // aiFlowsMetadata fetched by GitLabBackend#ensureServerDefaultFlow, so this
  // call is a cache hit rather than a second request when that ran first. A
  // transient failure returns null so a later call retries.
  async #getServerDefault(ctx: WorkflowStrategyContext): Promise<DefaultFlowConfig | undefined> {
    const serverDefault =
      (await ctx.workflowRunner.getDefaultFlow(
        ctx.newWorkflowMetadata.rootNamespaceId || undefined,
        ctx.newWorkflowMetadata.projectId,
      )) ?? undefined;
    if (serverDefault) {
      ctx.logger.info(`Server default flow: ${JSON.stringify(serverDefault)}`);
    } else {
      ctx.logger.debug('No server default flow advertised');
    }
    return serverDefault;
  }

  // The ungated retry keeps this no worse than reading the definition alone, since
  // the 19.3-gated query fails wholesale on an instance whose version we misread.
  async #fetchResumedFlowIdentity(
    ctx: WorkflowStrategyContext,
    sessionId: WorkflowId,
  ): Promise<ResumedFlowIdentity> {
    const variables = { workflowId: toGitLabGid('Ai::DuoWorkflows::Workflow', sessionId) };
    try {
      const data = await ctx.workflowRunner.getGraphqlData<DuoWorkflowFlowIdentity>({
        query: GET_WORKFLOW_FLOW_IDENTITY_QUERY,
        fragment: FLOW_METADATA_FRAGMENT,
        variables,
      });
      const node = requireNode(data, sessionId);
      return {
        definition: node.workflowDefinition ?? undefined,
        recordedFlow: parseRecordedFlowConfig(node),
      };
    } catch (error) {
      if (error instanceof ResumeIdentityError) throw error;
      ctx.logger.error(
        `Failed to read the recorded flow identity for session ${sessionId}; retrying without it`,
        error,
      );
    }

    try {
      const data = await ctx.workflowRunner.getGraphqlData<DuoWorkflowFlowIdentity>({
        query: GET_WORKFLOW_DEFINITION_QUERY,
        variables,
      });
      return { definition: requireNode(data, sessionId).workflowDefinition ?? undefined };
    } catch (error) {
      if (error instanceof ResumeIdentityError) throw error;
      // Guessing here resumes a developer session as chat: looks fine, never answers.
      throw new ResumeIdentityError(
        `Could not read the session record for ${sessionId}; refusing to resume it on an unknown flow.`,
        { cause: error },
      );
    }
  }
}

/** RunCondition (data) -> strategy (behaviour). The one place the axis is read. */
export function createWorkflowStrategy(
  condition: RunCondition,
  backendOpts: Pick<GitLabParsedOptions, 'workflowType' | 'legacy'>,
): WorkflowStrategy {
  if (condition.source === 'rails') {
    return new RailsPreCreatedWorkflowStrategy(condition.workflowId, backendOpts.workflowType);
  }
  // Only a headless run honours the explicit flow override; interactive always
  // obeys the server default. --legacy is interactive-only: headless `duo run`
  // already has --workflow-type / --flow-config-id to pin the flow.
  const isHeadless = condition.presentation === 'headless';
  const workflowTypeOverride = isHeadless ? backendOpts.workflowType : undefined;
  const legacy = !isHeadless && Boolean(backendOpts.legacy);
  return new LocalWorkflowStrategy(condition.resumeWorkflowId, workflowTypeOverride, legacy);
}
